import { Injectable } from '@angular/core';
import { IframeMessageType } from '@atlas-workspace/shared/models';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

export interface IframeMessage<T = unknown> {
  type: IframeMessageType;
  payload: T;
  timestamp: number;
}

@Injectable({
  providedIn: 'root',
})

export class IframeCommunicationService {
  private readonly _sentMessages$ = new BehaviorSubject<IframeMessage[]>([]);
  private readonly _incomingMessages$ = new Subject<IframeMessage>();
  //ToDO:  For now, allow all origins; replace '*' with a specific origin for N & Bao in production
  private readonly trustedParentOrigin = '*';
  private readonly handleParentMessageBound = this.handleParentMessage.bind(this);

  constructor() {
    window.addEventListener('message', this.handleParentMessageBound, false);
  }

  /**
   * Send a typed message to the parent window.
   * Adds it to the local queue as well.
   */
  public sendMessage<T>(type: IframeMessageType, payload: T): void {
    const message: IframeMessage<T> = {
      type,
      payload,
      timestamp: Date.now(),
    };
    try {
      window.parent.postMessage(message, this.trustedParentOrigin);
      this._sentMessages$.next([...this._sentMessages$.value, message]);
    } catch (err) {
      console.error(
        'IframeCommunicationService: Failed to send message to parent:',
        err
      );
    }
  }

  /**
   * Observable of all messages this iframe has sent so far.
   */
  public getSentMessages$(): Observable<IframeMessage[]> {
    return this._sentMessages$.asObservable();
  }

  /**
   * Observable of messages received from the parent window.
   */
  public getIncomingMessages$(): Observable<IframeMessage> {
    return this._incomingMessages$.asObservable();
  }

  /**
   * Cleanup method to remove the parent listener and complete streams.
   * Call this manually (e.g. from the component's ngOnDestroy).
   */
  public destroy(): void {
    window.removeEventListener('message', this.handleParentMessageBound);
    this._sentMessages$.complete();
    this._incomingMessages$.complete();
  }

  /**
   * Internal handler for messages coming from parent.
   * Validates origin (currently '*') and re-emits into incomingMessages$.
   */
  private handleParentMessage(event: MessageEvent): void {
    // ToDO: uncomment, when real origin is added.
    // if (event.origin !== this.trustedParentOrigin) { return; }

    const data = event.data as Partial<IframeMessage>;
    if (
      data &&
      typeof data.type === 'string' &&
      data.timestamp !== undefined
    ) {
      const message: IframeMessage = {
        type: data.type as IframeMessageType,
        payload: data.payload,
        timestamp: data.timestamp,
      };

      this._incomingMessages$.next(message);
    }
  }
}
